import React, { FC, RefObject, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { compose } from 'redux';

import { patchOffer } from 'src/inbox/ducks/offer';
import serializeCaptainOptions from 'src/inbox/utils/serializeCaptainOptions';
import getClassName from 'src/common/utils/getClassName';
import classNames from 'classnames';
import { ImmutableTrip } from '../../../../types/trips/Trips';
import { getOffer } from '../../../../common/utils/reduxStoreSelectors';
import Card from '../../presentation/Card';
import { modified as tripModified, offerModified, offerIsEmpty } from '../../../helpers';
import { open } from '../../../../common/ducks/zippy';
import { CAPTAIN_OPTION_IS_AUTO_PATCHED_FIELD, CAPTAINED_FIELD, UNSENT_CHANGES_WARNING } from '../../../constants';
import EditButton from '../../EditButton';
import NotificationMessage from '../../../../common/components/NotificationMessage';
import cardStyles from '../../presentation/Card/Card.module.scss';
import styles from './CaptainCard.module.scss';
import CaptainTemplate from '../../../../common/components/CaptainTemplate';
import useCaptainData from '../../../../common/hooks/useCaptainData';
import CaptainModal from './modal';

// Import multiple css modules into a component
const s = {
  ...cardStyles,
  ...styles,
};

const ERRORS = {
  CONFLICT: 'The Captain charter type conflicts with this Listing. Please select a valid charter type.',
  UNSET: 'Please select a captain charter type',
};

const captainedFields = [CAPTAINED_FIELD, 'captain_cost_included'];
const modalName = 'captain-modify';

type Props = {
  trip: ImmutableTrip;
  readOnly?: boolean;
  checkTripModification?: boolean;
  isEditOffer?: boolean;
  errorRef?: RefObject<HTMLDivElement>;
};

const CaptainCard: FC<Props> = ({
  trip, readOnly, checkTripModification, isEditOffer = false, errorRef,
}) => {
  const dispatch = useDispatch();
  const patchTheOffer = compose(dispatch, patchOffer);
  const offer = useSelector(getOffer);

  const {
    captainOptionHeading,
    showCaptainConflict,
    listingCaptainOptions,
    selectedCaptainOption,
  } = useCaptainData(trip, offer, isEditOffer);

  const showCaptainChoice = !captainOptionHeading.key;

  const openModal = () => dispatch(open(modalName));
  const offerIsModified = (
    !offerIsEmpty(offer)
    && !offer.get(CAPTAIN_OPTION_IS_AUTO_PATCHED_FIELD)
    && offerModified(offer, trip, captainedFields));
  const tripIsModified = checkTripModification && tripModified(trip, captainedFields);
  const modified = offerIsModified || tripIsModified;
  const isCaptainOptionAutoPatched = listingCaptainOptions?.length === 1
    && listingCaptainOptions[0]?.key === selectedCaptainOption;
  const disabled = readOnly || isCaptainOptionAutoPatched;

  useEffect(() => {
    if (listingCaptainOptions?.length === 1) {
      patchTheOffer({
        [CAPTAIN_OPTION_IS_AUTO_PATCHED_FIELD]: true,
        ...serializeCaptainOptions(listingCaptainOptions[0].key),
      });
    }
  }, [listingCaptainOptions, patchTheOffer]);

  return trip.get(CAPTAINED_FIELD) !== null ? (
    <Card
      highlighted={modified}
      classNameModifier={!disabled ? 'hoverEffect' : undefined}
      withoutMarginBottom
      withDashedBorderBottom
    >
      {/* Render notifications only when trip is editable */}
      {!disabled && (
        showCaptainChoice
        || (!showCaptainChoice && showCaptainConflict)
        || (offerIsModified && !showCaptainConflict))
      && (
        <NotificationMessage
          outdent={false}
          rootRef={errorRef}
        >
          {showCaptainChoice
            ? ERRORS.UNSET
            : showCaptainConflict && ERRORS.CONFLICT}
          {offerIsModified && !showCaptainConflict && UNSENT_CHANGES_WARNING}
        </NotificationMessage>
      )}

      {offerModified(offer, trip, captainedFields) && isCaptainOptionAutoPatched && !readOnly && (
        <NotificationMessage
          outdent={false}
          rootRef={errorRef}
        >
          {UNSENT_CHANGES_WARNING}
        </NotificationMessage>
      )}

      <CaptainTemplate>
        <p className={getClassName(s, 'details', classNames({ modified }))}>
          {captainOptionHeading.heading}
        </p>
        <p className={getClassName(s, 'snippet', classNames())}>
          {captainOptionHeading.subHeading}
        </p>
      </CaptainTemplate>

      {!disabled && (
        <>
          <EditButton openModal={openModal} />
          <CaptainModal
            isEditOffer={isEditOffer}
            modalName={modalName}
            trip={trip}
          />
        </>
      )}
    </Card>
  ) : null;
};

export default CaptainCard;
